import { useState, useEffect } from 'react';
import { FreightCardGridView } from './FreightCardGridView';
import { ETransportFreightPlutoDto, GetFreightsParams, MinimalTarget } from '@/generated/model';
import { getFreights } from '@/generated/client/etransport/etransport';
import { TargetFreightsDetail } from './TargetFreightsDetails';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { TargetFreightsSubmitForm } from './TargetFreightsSubmitForm';
import { FreightSubmitForm } from './FreightSubmitForm';
import { dbToArray } from '@/components/db/query';
import { Dialog } from '@mui/material';
import './etransport.css';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

export default function EtransportPopup({ close }: { close: () => void }) {
  // const { t } = useTranslation();
  // const userCtx = useContext(ctxUser);

  const [freights, setFreights] = useState<ETransportFreightPlutoDto[] | undefined>(undefined);
  const [targets, setTargets] = useState<MinimalTarget[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [newChangesAdded, setNewChangesAdded] = useState<FreightDataChange | null>(null);
  const [openTargetFreightSubmitForm, setOpenTargetFreightSubmitForm] = useState<boolean>(false);
  const [openTargetFreightDetails, setOpenTargetFreightDetails] = useState<boolean>(false);
  const [openFreightSubmitForm, setOpenFreightSubmitForm] = useState<boolean>(false);
  const [currentFreight, setCurrentFreight] = useState<ETransportFreightPlutoDto | undefined>(undefined);
  const [searchTarget, setSearchTarget] = useState<string | undefined>(undefined);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);
  const [isClosingDetails, setIsClosingDetails] = useState<boolean>(false);
  const [isClosingSubmitForm, setIsClosingSubmitForm] = useState<boolean>(false);

  useEffect(() => {
    async function fetchInitialData() {
      setIsLoading(true);
      try {
        const [freightsResult, targetsResult] = await Promise.all([getFreights(), dbToArray(0, 1000, 'asc')]);
        const freights: ETransportFreightPlutoDto[] = freightsResult.data;
        setFreights(freights);
        setTargets(targetsResult);
      } catch (err) {
        setFreights(undefined);
        setTargets(undefined);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!refetchTrigger) return;

    async function fetchInitialData() {
      setIsLoading(true);
      try {
        const [freightsResult, targetsResult] = await Promise.all([getFreights(), dbToArray(0, 1000, 'asc')]);
        const freights: ETransportFreightPlutoDto[] = freightsResult.data;
        setFreights(freights);
        setTargets(targetsResult);
        updateCurrentFreight(freights);
      } catch (err) {
        setFreights(undefined);
        setTargets(undefined);
        setCurrentFreight(undefined);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    function updateCurrentFreight(freights: ETransportFreightPlutoDto[]) {
      const freight: ETransportFreightPlutoDto = freights.filter((freight) => freight.uit === currentFreight?.uit)[0];
      setCurrentFreight(freight);
    }

    fetchInitialData();
  }, [refetchTrigger]);

  useEffect(() => {
    if (searchTarget === undefined) return;

    async function fetchFilteredFreights() {
      setIsLoading(true);
      const emptySearch: boolean = searchTarget === '';
      let filteredFreights: ETransportFreightPlutoDto[] = [];

      try {
        const result = await getFreights();
        filteredFreights = result.data;
      } catch (err) {
        console.error('Error fetching data:', err);
      }

      //Filter for UIT
      if (searchTarget !== undefined && !emptySearch) {
        filteredFreights = filteredFreights.filter((freight) => freight.uit?.includes(searchTarget)) || [];
      }

      //Filter for target
      try {
        if (!emptySearch) {
          const result = await getFreights({ filter: searchTarget } satisfies GetFreightsParams);
          const merged = [...result.data, ...filteredFreights];
          const seen = new Set<string>();
          filteredFreights = merged.filter((freight) => {
            if (!freight.uit || seen.has(freight.uit)) return false;
            seen.add(freight.uit);
            return true;
          });
        }

        setFreights(filteredFreights);
      } catch (err) {
        setFreights(undefined);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFilteredFreights();
  }, [searchTarget]);

  function handleRefetch(): void {
    setRefetchTrigger((prev) => prev + 1);
  }

  return isPhoneOrHighScreen() ? (
    <Dialog fullScreen open={true} onClose={close} className="popup-base-whole" hideBackdrop={true}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div
          className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-base phone"
          data-qa-element-id="etr_freight_card_grid"
        >
          <FreightCardGridView
            targets={targets}
            freights={freights}
            isLoading={isLoading}
            searchTarget={searchTarget}
            currentFreight={currentFreight}
            onRefetch={handleRefetch}
            setNewChanges={setNewChangesAdded}
            setSearchTarget={setSearchTarget}
            setCurrentFreight={setCurrentFreight}
            setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            setOpenTargetFreightDetails={setOpenTargetFreightDetails}
            setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
          />
        </div>

        {(openTargetFreightDetails || isClosingDetails) && (
          <div
            className={`etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-container phone ${!openTargetFreightDetails && isClosingDetails ? 'closing' : ''}`}
            data-qa-element-id="etr_freight_detail_panel"
          >
            {openTargetFreightSubmitForm ? (
              <TargetFreightsSubmitForm
                uit={currentFreight?.uit}
                targets={targets}
                minDate={new Date((currentFreight?.validityStartTimeMicros || 0) / 1000)}
                maxDate={new Date((currentFreight?.validityEndTimeMicros || 0) / 1000)}
                setNewChanges={setNewChangesAdded}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            ) : (
              <TargetFreightsDetail
                targets={targets}
                freight={currentFreight}
                newChange={newChangesAdded}
                openTargetFreightSubmitForm={openTargetFreightSubmitForm}
                onRefetch={handleRefetch}
                setNewChanges={setNewChangesAdded}
                setCurrentFreight={setCurrentFreight}
                setIsClosingDetails={setIsClosingDetails}
                setOpenTargetFreightDetails={setOpenTargetFreightDetails}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            )}
          </div>
        )}

        {(openFreightSubmitForm || isClosingSubmitForm) && (
          <div
            className={`etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-container phone ${!openFreightSubmitForm && isClosingSubmitForm ? 'closing' : ''}`}
            data-qa-element-id="etr_freight_submit_panel"
          >
            <FreightSubmitForm
              targets={targets}
              onRefetch={handleRefetch}
              setCurrentFreight={setCurrentFreight}
              setIsClosingSubmitForm={setIsClosingSubmitForm}
              setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            />
          </div>
        )}
      </div>
    </Dialog>
  ) : (
    <div style={{ position: 'relative', width: '500px' }}>
      <div
        className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-base"
        data-qa-element-id="etr_freight_card_grid"
      >
        <FreightCardGridView
          targets={targets}
          freights={freights}
          isLoading={isLoading}
          searchTarget={searchTarget}
          currentFreight={currentFreight}
          onRefetch={handleRefetch}
          setNewChanges={setNewChangesAdded}
          setSearchTarget={setSearchTarget}
          setCurrentFreight={setCurrentFreight}
          setOpenFreightSubmitForm={setOpenFreightSubmitForm}
          setOpenTargetFreightDetails={setOpenTargetFreightDetails}
          setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
        />
      </div>
      {(openTargetFreightDetails || isClosingDetails) && (
        <div
          className={`etransport-overlay-container ${!openTargetFreightDetails && isClosingDetails ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_detail_panel"
          >
            <TargetFreightsDetail
              targets={targets}
              freight={currentFreight}
              newChange={newChangesAdded}
              openTargetFreightSubmitForm={openTargetFreightSubmitForm}
              onRefetch={handleRefetch}
              setNewChanges={setNewChangesAdded}
              setCurrentFreight={setCurrentFreight}
              setIsClosingDetails={setIsClosingDetails}
              setOpenTargetFreightDetails={setOpenTargetFreightDetails}
              setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
            />
          </div>
          {openTargetFreightSubmitForm && (
            <div className="etransport-freight-panel" data-qa-element-id="etr_freight_target_submit_panel">
              <TargetFreightsSubmitForm
                uit={currentFreight?.uit}
                targets={targets}
                minDate={new Date((currentFreight?.validityStartTimeMicros || 0) / 1000)}
                maxDate={new Date((currentFreight?.validityEndTimeMicros || 0) / 1000)}
                setNewChanges={setNewChangesAdded}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            </div>
          )}
        </div>
      )}
      {(openFreightSubmitForm || isClosingSubmitForm) && (
        <div
          className={`etransport-overlay-container ${!openFreightSubmitForm && isClosingSubmitForm ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_submit_panel"
          >
            <FreightSubmitForm
              targets={targets}
              onRefetch={handleRefetch}
              setCurrentFreight={setCurrentFreight}
              setIsClosingSubmitForm={setIsClosingSubmitForm}
              setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            />
          </div>
        </div>
      )}
    </div>
  );
}
